import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Wifi, WifiOff, Archive, ArchiveRestore, Trash2, Plus, Package, ShoppingCart, Unlink, ShieldOff, ShieldCheck } from 'lucide-react';
import { DEVICE_TYPES, PRINTER_SUBTYPES, CONNECTION_TYPES, CONSUMABLE_PRESETS, SWITCH_MANAGED_TYPES, deviceTypeInfo, printerSubtypeLabel, connectionTypeLabel } from '../utils/deviceConfig';

// Felder, die bei Ninja-Geraeten (is_third_party = false) nicht editierbar sind --
// muss mit NINJA_OWNED_FIELDS in backend/src/routes/devices.js uebereinstimmen.
const NINJA_OWNED_FIELDS = ['display_name', 'ip_address'];

export default function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [newConsumableName, setNewConsumableName] = useState('');
  const [addingCustom, setAddingCustom] = useState(false);
  const [newUsvName, setNewUsvName] = useState('');
  const [newUsvType, setNewUsvType] = useState('');
  const [newMonitorSize, setNewMonitorSize] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/devices/' + id);
      setDevice(res.data);
      setForm({
        display_name: res.data.display_name || '',
        device_type: res.data.device_type || '',
        device_subtype: res.data.device_subtype || '',
        is_multifunction: res.data.is_multifunction || false,
        connection_type: res.data.connection_type || '',
        location: res.data.location || '',
        serial_number: res.data.serial_number || '',
        notes: res.data.notes || '',
        purchase_date: res.data.purchase_date ? res.data.purchase_date.substring(0, 10) : '',
        ip_address: res.data.ip_address || '',
        switch_port_count: res.data.switch_port_count || '',
        switch_managed: res.data.switch_managed || '',
      });
    } catch (e) {
      toast.error('Geraet nicht gefunden');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Ninja-eigene Felder bei Ninja-Geraeten gar nicht erst mitschicken -- die
      // Backend-Route wuerde sie sowieso mit 400 ablehnen.
      if (!device.is_third_party) {
        NINJA_OWNED_FIELDS.forEach(f => delete payload[f]);
      }
      if (payload.device_type !== 'printer') payload.device_subtype = null;
      if (payload.device_type !== 'switch') {
        payload.switch_port_count = null;
        payload.switch_managed = null;
      }
      const res = await api.put('/devices/' + id, payload);
      setDevice(prev => ({ ...prev, ...res.data }));
      toast.success('Gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Geraet wirklich archivieren?')) return;
    try {
      await api.delete('/devices/' + id);
      toast.success('Geraet archiviert');
      load();
    } catch (e) {
      toast.error('Fehler beim Archivieren');
    }
  };

  const handleReactivate = async () => {
    try {
      await api.post('/devices/' + id + '/reactivate');
      toast.success('Geraet reaktiviert');
      load();
    } catch (e) {
      toast.error('Fehler beim Reaktivieren');
    }
  };

  const handleToggleResponsibility = async () => {
    try {
      const res = await api.post('/devices/' + id + '/toggle-responsibility');
      setDevice(prev => ({ ...prev, ...res.data }));
      toast.success(res.data.not_our_responsibility ? 'Als nicht betreut markiert' : 'Wieder als betreut markiert');
    } catch (e) {
      toast.error('Fehler beim Markieren');
    }
  };

  const handleConvertToThirdParty = async () => {
    if (!window.confirm('Geraet unwiderruflich von NinjaOne loesen und als Fremdgeraet fuehren?\n\nNur verwenden, wenn das Geraet in NinjaOne wirklich geloescht wurde -- der naechste Sync kann es danach nicht mehr automatisch aktualisieren. Ticket-/Journal-Verknuepfungen bleiben erhalten.')) return;
    try {
      await api.post('/devices/' + id + '/convert-to-third-party');
      toast.success('In Fremdgeraet umgewandelt');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler beim Umwandeln');
    }
  };

  const handleHardDelete = async () => {
    if (!window.confirm('Geraet UNWIDERRUFLICH loeschen? Die Historie in Tickets/Journal bleibt als Namens-Schnappschuss erhalten.')) return;
    try {
      await api.delete('/devices/' + id + '/hard');
      toast.success('Geraet endgueltig geloescht');
      navigate('/devices');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler beim Loeschen');
    }
  };

  const addConsumable = async (name, consumable_type, isCustom) => {
    try {
      await api.post('/devices/' + id + '/consumables', { name, consumable_type, is_custom: !!isCustom });
      toast.success('Verbrauchsmaterial hinzugefuegt');
      setNewConsumableName('');
      setAddingCustom(false);
      load();
    } catch (e) {
      toast.error('Fehler beim Hinzufuegen');
    }
  };

  const deleteConsumable = async (consumableId) => {
    if (!window.confirm('Verbrauchsmaterial wirklich entfernen?')) return;
    try {
      await api.delete('/devices/consumables/' + consumableId);
      toast.success('Entfernt');
      load();
    } catch (e) {
      toast.error('Fehler beim Entfernen');
    }
  };

  const handleAddUsvBattery = async () => {
    if (!newUsvName.trim()) return;
    await addConsumable(newUsvName.trim(), newUsvType.trim() || null, true);
    setNewUsvName('');
    setNewUsvType('');
  };

  // Bildschirmgroesse bei device_type = 'monitor' -- analog USV-Akku-Pattern
  // (freies Eingabefeld statt Presets, siehe Vault-Notiz "Geraeteverwaltung (geplant)").
  const handleAddMonitorSize = async () => {
    if (!newMonitorSize.trim()) return;
    await addConsumable('Bildschirmgröße', newMonitorSize.trim(), true);
    setNewMonitorSize('');
  };

  const orderConsumable = async (consumableId) => {
    try {
      await api.post('/devices/consumables/' + consumableId + '/orders', { quantity: 1 });
      toast.success('Bestellung vermerkt');
      load();
    } catch (e) {
      toast.error('Fehler beim Vermerken');
    }
  };

  if (loading) return <div className="text-gray-400 text-sm">Laden...</div>;
  if (!device) return <div className="text-gray-400 text-sm">Geraet nicht gefunden</div>;

  const isThirdParty = device.is_third_party;
  const typeInfo = deviceTypeInfo(device.device_type);
  const Icon = typeInfo.icon;
  const isUsv = form.device_type === 'usv';
  const isMonitor = form.device_type === 'monitor';
  const isSwitch = form.device_type === 'switch';
  const presets = form.device_type === 'printer' && form.device_subtype ? (CONSUMABLE_PRESETS[form.device_subtype] || []) : [];
  const existingNames = (device.consumables || []).map(c => c.name);

  return (
    <div>
      <button onClick={() => navigate('/devices')}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 mb-4 transition-colors">
        <ArrowLeft size={16} /> Zurueck
      </button>

      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-gray-100 rounded-xl p-3">
            <Icon size={20} className="text-gray-500" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-gray-900">
                {device.display_name || device.system_name || device.dns_name || ('Geraet ' + device.id)}
              </h2>
              {isThirdParty && (
                <span className="text-xs font-medium bg-fuchsia-50 text-fuchsia-600 px-2 py-0.5 rounded-full">Fremdgeraet</span>
              )}
              {device.not_our_responsibility && (
                <span className="text-xs font-medium bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Nicht betreut</span>
              )}
              {!device.is_active && (
                <span className="text-xs font-medium bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Archiviert</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {typeInfo.label}
              {device.customer_name && (
                <> · <Link to={'/customers/' + device.customer_id} className="hover:text-primary-600">{device.customer_name}</Link></>
              )}
              {!isThirdParty && (
                <> · {device.offline
                  ? <span className="text-red-500"><WifiOff size={11} className="inline mr-1" />Offline</span>
                  : <span className="text-teal-600"><Wifi size={11} className="inline mr-1" />Online</span>}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleToggleResponsibility}
            className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium px-3 py-2 rounded-lg transition-colors">
            {device.not_our_responsibility ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
            {device.not_our_responsibility ? 'Wieder betreut' : 'Nicht betreut markieren'}
          </button>
          {!isThirdParty && (
            <button onClick={handleConvertToThirdParty}
              className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium px-3 py-2 rounded-lg transition-colors">
              <Unlink size={14} /> In Fremdgeraet umwandeln
            </button>
          )}
          {device.is_active ? (
            <button onClick={handleArchive}
              className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium px-3 py-2 rounded-lg transition-colors">
              <Archive size={14} /> Archivieren
            </button>
          ) : (
            <>
              <button onClick={handleReactivate}
                className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium px-3 py-2 rounded-lg transition-colors">
                <ArchiveRestore size={14} /> Reaktivieren
              </button>
              <button onClick={handleHardDelete}
                className="flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 text-xs font-medium px-3 py-2 rounded-lg transition-colors">
                <Trash2 size={14} /> Endgueltig loeschen
              </button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
        <h3 className="font-semibold text-gray-900 text-sm mb-3">Details</h3>
        {!isThirdParty && (
          <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mb-3">
            Name und IP-Adresse werden automatisch aus NinjaOne synchronisiert und koennen hier nicht bearbeitet werden.
            Alle anderen Felder sind frei editierbar.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Name {isThirdParty ? '*' : ''}</label>
            <input type="text" value={isThirdParty ? form.display_name : (device.display_name || device.system_name || '')}
              disabled={!isThirdParty}
              onChange={e => setForm({ ...form, display_name: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Geraetetyp</label>
            <select value={form.device_type} onChange={e => setForm({ ...form, device_type: e.target.value, device_subtype: '' })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">-- waehlen --</option>
              {DEVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {form.device_type === 'printer' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Drucker-Subtyp</label>
              <select value={form.device_subtype} onChange={e => setForm({ ...form, device_subtype: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">-- waehlen --</option>
                {PRINTER_SUBTYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          )}
          {form.device_type === 'printer' && (
            <div className="flex items-center gap-2 mt-5">
              <input type="checkbox" id="is_multifunction" checked={form.is_multifunction}
                onChange={e => setForm({ ...form, is_multifunction: e.target.checked })} className="rounded border-gray-300" />
              <label htmlFor="is_multifunction" className="text-sm text-gray-700">Multifunktionsgeraet (MFC)</label>
            </div>
          )}
          {isSwitch && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Anzahl Ports</label>
              <input type="text" value={form.switch_port_count} onChange={e => setForm({ ...form, switch_port_count: e.target.value })}
                placeholder="z.B. 24"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          )}
          {isSwitch && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Managed/Unmanaged</label>
              <select value={form.switch_managed} onChange={e => setForm({ ...form, switch_managed: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">-- waehlen --</option>
                {SWITCH_MANAGED_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Verbindung</label>
            <select value={form.connection_type} onChange={e => setForm({ ...form, connection_type: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">-- waehlen --</option>
              {CONNECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Standort</label>
            <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Seriennummer</label>
            <input type="text" value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">IP-Adresse</label>
            <input type="text" value={isThirdParty ? form.ip_address : (device.ip_address || '')}
              disabled={!isThirdParty}
              onChange={e => setForm({ ...form, ip_address: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Kaufdatum</label>
            <input type="date" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Notizen</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
          </div>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="mt-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
          {saving ? 'Wird gespeichert...' : 'Speichern'}
        </button>
      </div>

      {(form.device_type === 'printer' || isUsv || isMonitor) && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-4">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <Package size={16} className="text-gray-400" />
            <h3 className="font-semibold text-gray-900 text-sm">
              {isUsv ? 'Akkus' : isMonitor ? 'Bildschirmgröße' : 'Verbrauchsmaterial'}
            </h3>
          </div>

          {isUsv && (
            <div className="p-4 border-b border-gray-100">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input type="text" value={newUsvName} onChange={e => setNewUsvName(e.target.value)}
                  placeholder="Bezeichnung (z.B. Haupt-Akku)"
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
                <input type="text" value={newUsvType} onChange={e => setNewUsvType(e.target.value)}
                  placeholder="Typ (z.B. RBC124, 12V 9Ah)"
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <button onClick={handleAddUsvBattery}
                className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 px-3 py-1.5 rounded-lg transition-colors">
                <Plus size={12} /> Akku hinzufuegen
              </button>
            </div>
          )}

          {isMonitor && (
            <div className="p-4 border-b border-gray-100">
              <div className="flex gap-2 mb-2">
                <input type="text" value={newMonitorSize} onChange={e => setNewMonitorSize(e.target.value)}
                  placeholder="Größe (z.B. 27 Zoll)"
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500 flex-1" />
              </div>
              <button onClick={handleAddMonitorSize}
                className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 px-3 py-1.5 rounded-lg transition-colors">
                <Plus size={12} /> Größe hinterlegen
              </button>
            </div>
          )}

          {presets.length > 0 && (
            <div className="p-4 border-b border-gray-100 flex flex-wrap gap-2">
              {presets.filter(p => !existingNames.includes(p.name)).map(p => (
                <button key={p.consumable_type} onClick={() => addConsumable(p.name, p.consumable_type, false)}
                  className="flex items-center gap-1.5 text-xs font-medium border border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-600 px-3 py-1.5 rounded-lg transition-colors">
                  <Plus size={12} /> {p.name}
                </button>
              ))}
              {addingCustom ? (
                <div className="flex items-center gap-2">
                  <input type="text" value={newConsumableName} onChange={e => setNewConsumableName(e.target.value)}
                    placeholder="Eigenes Verbrauchsmaterial..." autoFocus
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  <button onClick={() => newConsumableName.trim() && addConsumable(newConsumableName.trim(), null, true)}
                    className="text-xs font-medium bg-primary-500 hover:bg-primary-600 text-white px-3 py-1.5 rounded-lg">Anlegen</button>
                </div>
              ) : (
                <button onClick={() => setAddingCustom(true)}
                  className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 px-3 py-1.5 rounded-lg transition-colors">
                  <Plus size={12} /> Individuell
                </button>
              )}
            </div>
          )}
          {presets.length === 0 && form.device_subtype && (
            <div className="p-4 border-b border-gray-100">
              {addingCustom ? (
                <div className="flex items-center gap-2">
                  <input type="text" value={newConsumableName} onChange={e => setNewConsumableName(e.target.value)}
                    placeholder="Verbrauchsmaterial-Name..." autoFocus
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  <button onClick={() => newConsumableName.trim() && addConsumable(newConsumableName.trim(), null, true)}
                    className="text-xs font-medium bg-primary-500 hover:bg-primary-600 text-white px-3 py-1.5 rounded-lg">Anlegen</button>
                </div>
              ) : (
                <button onClick={() => setAddingCustom(true)}
                  className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 px-3 py-1.5 rounded-lg transition-colors">
                  <Plus size={12} /> Verbrauchsmaterial hinzufuegen
                </button>
              )}
            </div>
          )}

          <div className="divide-y divide-gray-50">
            {(device.consumables || []).length === 0 && (
              <p className="text-sm text-gray-400 p-4">
                {isUsv ? 'Noch kein Akku hinterlegt' : isMonitor ? 'Noch keine Größe hinterlegt' : 'Noch kein Verbrauchsmaterial hinterlegt'}
              </p>
            )}
            {(device.consumables || []).map(c => (
              <div key={c.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {c.name}{(isUsv || isMonitor) && c.consumable_type ? ' · ' + c.consumable_type : ''}
                    </p>
                    {c.orders && c.orders.length > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Letzte Bestellung: {new Date(c.orders[0].ordered_at).toLocaleDateString('de-DE')} · {c.orders[0].quantity}x · {c.orders[0].status}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!isMonitor && (
                      <button onClick={() => orderConsumable(c.id)}
                        className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 px-3 py-1.5 rounded-lg hover:bg-primary-50 transition-colors">
                        <ShoppingCart size={13} /> Bestellung vermerken
                      </button>
                    )}
                    <button onClick={() => deleteConsumable(c.id)}
                      className="text-gray-300 hover:text-red-400 transition-colors p-1.5">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
